import { toast } from "sonner";
import { getCauseDisplay } from "../components/telemetry-outage/utils";
import { useOutageNotificationStore } from "../stores/useOutageNotificationStore";
import type { NewOutageNotification } from "../stores/useOutageNotificationStore";

/**
 * In-app-only notification handling for admin outage events (§6.3, v1.6 of
 * the FE push architecture spec) — a distinct toast for an outage opening
 * vs. resolving, plus a log entry for the bell-icon panel (JAA-1096) to
 * render later. No native Notification/Web Push — ruled out, not deferred,
 * see JAA-1061 (closed).
 *
 * Deliberately only ever called from useFleetOutageStream's live
 * event:outage handler, never from the initial GET /admin/telemetry-outages
 * snapshot — that seeds current state, not a notification history, so
 * mounting with three already-ongoing outages must not fire three toasts.
 * Admin-only gating is structural, not re-checked here: useFleetOutageStream
 * only ever receives events at all when enabled (platform-admin), so this
 * handler has no code path a non-admin session can reach.
 */
export function createOutageNotificationHandler(): (
  notification: NewOutageNotification,
) => void {
  return function handleOutageNotification(notification) {
    const facilityLabel = notification.facilityName ?? "A facility";
    const causeLabel = getCauseDisplay(notification.causeAtDetection).label;

    if (notification.isOngoing) {
      toast.error(`${facilityLabel} outage detected`, {
        description: causeLabel,
      });
    } else {
      toast.success(`${facilityLabel} outage resolved`, {
        description: causeLabel,
      });
    }

    useOutageNotificationStore.getState().addEntry(notification);
  };
}
